import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, renderList as _renderList, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "ui-multiselect__required"
}
const _hoisted_3 = ["id"]
const _hoisted_4 = { class: "ui-multiselect__selected" }
const _hoisted_5 = { class: "ui-multiselect__compact-text" }
const _hoisted_6 = {
  key: 0,
  class: "ui-multiselect__compact-badge"
}
const _hoisted_7 = ["onClick", "disabled"]
const _hoisted_8 = {
  key: 0,
  class: "ui-multiselect__more"
}
const _hoisted_9 = {
  key: 1,
  class: "ui-multiselect__placeholder"
}
const _hoisted_10 = { class: "ui-multiselect__icon" }
const _hoisted_11 = {
  key: 0,
  class: "ui-multiselect__search"
}
const _hoisted_12 = ["placeholder"]
const _hoisted_13 = {
  key: 0,
  class: "ui-multiselect__group-header"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "ui-multiselect__option-checkbox" }
const _hoisted_16 = { class: "ui-multiselect__option-label" }
const _hoisted_17 = ["title"]
const _hoisted_18 = {
  key: 0,
  class: "ui-multiselect__no-results"
}
const _hoisted_19 = {
  key: 1,
  class: "ui-multiselect__footer"
}
const _hoisted_20 = {
  key: 1,
  class: "ui-multiselect__helper"
}
const _hoisted_21 = {
  key: 0,
  class: "ui-multiselect__error"
}
const _hoisted_22 = {
  key: 1,
  class: "ui-multiselect__helper-text"
}

import { Icon } from "@iconify/vue";
import { Teleport, computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { SelectOption, Size } from "./types";

interface Props {
  modelValue?: (string | number)[];
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  compact?: boolean;
  maxDisplayChips?: number;
  showSelectAll?: boolean;
  selectAllText?: string;
  noResultsText?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MultiSelect',
  props: {
    modelValue: { default: () => [] },
    label: {},
    placeholder: { default: "Select options" },
    options: {},
    size: { default: "md" },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    error: {},
    helperText: {},
    id: {},
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { default: "Search..." },
    compact: { type: Boolean, default: false },
    maxDisplayChips: { default: 3 },
    showSelectAll: { type: Boolean, default: false },
    selectAllText: { default: "Select All" },
    noResultsText: { default: "No options found" }
  },
  emits: ["update:modelValue", "blur", "focus", "change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const isFocused = ref(false);
const searchQuery = ref("");
const dropdownStyle = ref<Record<string, string>>({});
const containerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const optionsRef = ref<HTMLElement | null>(null);

const selectId = computed(
  () => props.id || `multiselect-${Math.random().toString(36).slice(2, 11)}`
);

const selectedValues = computed(() => props.modelValue || []);

const selectedOptions = computed(() => {
  return props.options.filter((option) => selectedValues.value.includes(option.value));
});

const displaySelected = computed(() => {
  return selectedOptions.value.slice(0, props.maxDisplayChips);
});

const remainingCount = computed(() => {
  return Math.max(0, selectedOptions.value.length - props.maxDisplayChips);
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.options;
  }
  const query = searchQuery.value.toLowerCase();
  return props.options.filter((option) => option.label.toLowerCase().includes(query));
});

// Build grouped items: inserts group headers before each new group
const groupedItems = computed(() => {
  const items: any[] = [];
  let lastGroup: string | undefined = undefined;
  for (const opt of filteredOptions.value) {
    if (opt.group && opt.group !== lastGroup) {
      items.push({ _groupHeader: true, group: opt.group });
      lastGroup = opt.group;
    }
    items.push(opt);
  }
  return items;
});

const isSelected = (value: string | number) => {
  return selectedValues.value.includes(value);
};

const selectAllOptions = computed(() => {
  return filteredOptions.value.filter((opt) => !opt.disabled);
});

const areAllSelected = computed(() => {
  if (selectAllOptions.value.length === 0) return false;
  return selectAllOptions.value.every((opt) => isSelected(opt.value));
});

const wrapperClasses = computed(() => {
  const classes = ["ui-multiselect"];
  classes.push(`ui-multiselect--${props.size}`);
  if (props.disabled) classes.push("ui-multiselect--disabled");
  if (props.error) classes.push("ui-multiselect--error");
  return classes;
});

const triggerClasses = computed(() => {
  const classes = ["ui-multiselect__trigger"];
  if (isFocused.value || isOpen.value) classes.push("ui-multiselect__trigger--focused");
  if (props.disabled) classes.push("ui-multiselect__trigger--disabled");
  return classes;
});

const toggleOption = (option: SelectOption) => {
  if (props.disabled || option.disabled) return;

  const currentValues = [...selectedValues.value];
  const index = currentValues.indexOf(option.value);

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(option.value);
  }

  emit("update:modelValue", currentValues);
  emit("change", currentValues);
};

const removeOption = (value: string | number) => {
  if (props.disabled) return;

  const currentValues = selectedValues.value.filter((v) => v !== value);
  emit("update:modelValue", currentValues);
  emit("change", currentValues);
};

const toggleSelectAll = () => {
  if (props.disabled) return;

  let newValues: (string | number)[];
  if (areAllSelected.value) {
    // Deselect all filtered options
    const filteredValues = selectAllOptions.value.map((opt) => opt.value);
    newValues = selectedValues.value.filter((v) => !filteredValues.includes(v));
  } else {
    // Select all filtered options
    const filteredValues = selectAllOptions.value.map((opt) => opt.value);
    const existingValues = selectedValues.value.filter((v) => !filteredValues.includes(v));
    newValues = [...existingValues, ...filteredValues];
  }

  emit("update:modelValue", newValues);
  emit("change", newValues);
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = "";
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const updateDropdownPosition = () => {
  if (!containerRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = dropdownRef.value?.offsetHeight || 300;
  const dropdownWidth = Math.max(rect.width, 280);

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  const maxLeft = Math.max(spacing, window.innerWidth - dropdownWidth - spacing);
  const clampedLeft = Math.min(Math.max(spacing, rect.left), maxLeft);

  const topPosition = shouldShowAbove
    ? Math.max(spacing, rect.top - dropdownHeight - spacing)
    : Math.min(window.innerHeight - dropdownHeight - spacing, rect.bottom + spacing);

  dropdownStyle.value = {
    position: "fixed",
    top: `${topPosition}px`,
    left: `${clampedLeft}px`,
    width: `${dropdownWidth}px`,
    zIndex: "10070",
  };
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (
    target &&
    !target.closest(".ui-multiselect__container") &&
    !target.closest(".ui-multiselect__dropdown")
  ) {
    isOpen.value = false;
  }
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".ui-multiselect__container") &&
    !relatedTarget.closest(".ui-multiselect__dropdown")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      isFocused.value = false;
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      isFocused.value = false;
      emit("blur", event);
    }, 100);
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

watch(
  () => props.options,
  () => {
    if (isOpen.value) {
      nextTick(() => {
        updateDropdownPosition();
      });
    }
  },
  { deep: true }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(wrapperClasses.value)
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: selectId.value,
          class: "ui-multiselect__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "ui-multiselect__container",
      ref_key: "containerRef",
      ref: containerRef
    }, [
      _createElementVNode("div", {
        id: selectId.value,
        class: _normalizeClass(triggerClasses.value),
        onClick: toggleDropdown,
        onFocus: handleFocus,
        onBlur: handleBlur,
        tabindex: "0"
      }, [
        _createElementVNode("div", _hoisted_4, [
          (selectedOptions.value.length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (__props.compact)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("span", _hoisted_5, _toDisplayString(selectedOptions.value[0].label), 1),
                      (selectedOptions.value.length > 1)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_6, " +" + _toDisplayString(selectedOptions.value.length - 1), 1))
                        : _createCommentVNode("", true)
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displaySelected.value, (option, index) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: `${option.value}-${index}`,
                          class: "ui-multiselect__chip"
                        }, [
                          _createTextVNode(_toDisplayString(option.label) + " ", 1),
                          _createElementVNode("button", {
                            type: "button",
                            class: "ui-multiselect__chip-remove",
                            onClick: _withModifiers(($event: any) => (removeOption(option.value)), ["stop"]),
                            disabled: __props.disabled
                          }, [
                            _createVNode(_unref(Icon), { icon: "mdi:close" })
                          ], 8, _hoisted_7)
                        ]))
                      }), 128)),
                      (remainingCount.value > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, " +" + _toDisplayString(remainingCount.value) + " more ", 1))
                        : _createCommentVNode("", true)
                    ], 64))
              ], 64))
            : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(__props.placeholder), 1))
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(_unref(Icon), {
            icon: "mdi:chevron-down",
            class: _normalizeClass({ 'ui-multiselect__icon--rotated': isOpen.value })
          }, null, 8, ["class"])
        ])
      ], 42, _hoisted_3),
      (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "ui-multiselect__dropdown",
              style: _normalizeStyle(dropdownStyle.value),
              ref_key: "dropdownRef",
              ref: dropdownRef
            }, [
              (__props.searchable)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:magnify",
                      class: "ui-multiselect__search-icon"
                    }),
                    _withDirectives(_createElementVNode("input", {
                      type: "text",
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                      placeholder: __props.searchPlaceholder,
                      class: "ui-multiselect__search-input",
                      onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                    }, null, 8, _hoisted_12), [
                      [_vModelText, searchQuery.value]
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: "ui-multiselect__options",
                ref_key: "optionsRef",
                ref: optionsRef
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedItems.value, (item) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: item._groupHeader ? `header-${item.group}` : item.value
                  }, [
                    (item._groupHeader)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(item.group), 1))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: _normalizeClass([
                  'ui-multiselect__option',
                  {
                    'ui-multiselect__option--selected': isSelected(item.value),
                    'ui-multiselect__option--disabled': item.disabled,
                  },
                ]),
                          onClick: _withModifiers(($event: any) => (toggleOption(item)), ["stop"])
                        }, [
                          _createElementVNode("div", _hoisted_15, [
                            (isSelected(item.value))
                              ? (_openBlock(), _createBlock(_unref(Icon), {
                                  key: 0,
                                  icon: "mdi:check",
                                  width: "12",
                                  height: "12",
                                  class: "ui-multiselect__option-check"
                                }))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("span", _hoisted_16, _toDisplayString(item.label), 1),
                          (item.description)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: "ui-multiselect__info-icon",
                                title: item.description,
                                onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                              }, [
                                _createVNode(_unref(Icon), {
                                  icon: "mdi:information-outline",
                                  width: "14",
                                  height: "14"
                                })
                              ], 8, _hoisted_17))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_14))
                  ], 64))
                }), 128)),
                (filteredOptions.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, _toDisplayString(__props.noResultsText), 1))
                  : _createCommentVNode("", true)
              ], 512),
              (__props.showSelectAll)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "ui-multiselect__select-all",
                      onClick: _withModifiers(toggleSelectAll, ["stop"])
                    }, _toDisplayString(__props.selectAllText), 1)
                  ]))
                : _createCommentVNode("", true)
            ], 4))
          : _createCommentVNode("", true)
      ]))
    ], 512),
    (__props.error || __props.helperText)
      ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
          (__props.error)
            ? (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(__props.error), 1))
            : (__props.helperText)
              ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(__props.helperText), 1))
              : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})